import { Box, Divider, Grid, Tab, Tabs } from "@mui/material";
import { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import ScrollableFlexContainer from "@entrilia/shared/components/ScrollableFlexContainer.tsx";
import type {
  DraggableMetaType,
  FieldWrapper,
  PivotColumnResponse,
  ReportField,
} from "../../../../../../../../api/types/svTypes.ts";
import { useAppDispatch } from "../../../../store/hooks/useAppDispatch";
import { selectColumns, selectRows, selectSorts } from "../../../../store/selectors/fieldsStateSelectors";
import { selectEditDisabled, selectSelectedBuilderTab } from "../../../../store/selectors/uiSettingsSelectors";
import type { BuilderTab } from "../../../../store/types/uiSettingsState.types";
import { createUiSettingsAction } from "../../../../store/utils/storeUtils";
import type { TableComponentProps } from "../../../viewer/common/Types.ts";
import useCanAddCustomFields from "../../common/hooks/useCanAddCustomFields.ts";
import SortFieldsContainer from "../../common/sorting/SortFieldsContainer.tsx";
import { extractMeta, isValueField } from "../../common/utilities/dropFieldContainerHelper.ts";
import { isDimensionMeta, isMeasureMeta } from "../../common/utilities/metaUtils.ts";
import ColumnsDropFieldsContainer from "./columns/ColumnsDropFieldsContainer.tsx";
import ConditionsDropFieldsContainer from "./conditions/ConditionsDropFieldsContainer.tsx";
import FormatContainer from "./format/FormatContainer.tsx";
import RowsDropFieldsContainer from "./rows/RowsDropFieldsContainer.tsx";
import SettingsContainer from "./settings/SettingsContainer.tsx";
import ValuesDropFieldsContainer from "./values/ValuesDropFieldsContainer.tsx";

interface Props extends TableComponentProps {
  pivotColumns?: PivotColumnResponse[];
}
export const TableComponent = ({ ReportTypeSelector, pivotColumns }: Props) => {
  const rowsAreaValues = useSelector(selectRows);
  const columnsAreaValues = useSelector(selectColumns);
  const sortAreaValues = useSelector(selectSorts);
  const editDisabled = useSelector(selectEditDisabled);

  const dispatch = useAppDispatch();
  const selectedTab = useSelector(selectSelectedBuilderTab);
  const canAddCustomField = useCanAddCustomFields();

  const availableFields = useMemo(() => {
    const result = rowsAreaValues
      .concat(columnsAreaValues)
      .filter((f) => !sortAreaValues.find((v) => v.meta.name === f.meta.name));
    return result;
  }, [rowsAreaValues, columnsAreaValues, sortAreaValues]);

  const fields = useMemo(() => {
    return rowsAreaValues.concat(columnsAreaValues);
  }, [rowsAreaValues, columnsAreaValues]);

  const handleCanDropItem = useCallback((item: FieldWrapper<ReportField> | DraggableMetaType) => {
    if (isValueField(item)) {
      return true;
    }
    const meta = extractMeta(item);
    if (!meta) {
      return false;
    }
    if (isDimensionMeta(meta)) {
      return true;
    }
    if (isMeasureMeta(meta) && !meta.capabilities.isScalar) {
      return true;
    }
    return true;
  }, []);

  return (
    <Box sx={{ display: "flex", flex: 1, flexDirection: "column", gap: "1rem", mt: "1rem" }}>
      <Grid container sx={{ px: "1.3rem", width: "100%" }}>
        {ReportTypeSelector}
        <Tabs
          value={selectedTab}
          sx={{ borderBottom: "none" }}
          onChange={(_, value: BuilderTab) => dispatch(createUiSettingsAction("setTab", value))}
        >
          <Tab value="Fields" label="Fields" />
          <Tab value="Settings" label="Settings" />
          <Tab value="Format" label="Format" />
        </Tabs>
      </Grid>
      <Divider sx={{ mt: -2 }} />
      {selectedTab === "Fields" && (
        <ScrollableFlexContainer
          scrollContainerSx={{ display: "flex", flexDirection: "column", gap: "1.5rem", px: "1.3rem" }}
        >
          <ConditionsDropFieldsContainer disableEqualitySelection={true} readonlyMode={editDisabled} />
          <RowsDropFieldsContainer readonlyMode={editDisabled} />
          <ColumnsDropFieldsContainer readonlyMode={editDisabled} />
          <ValuesDropFieldsContainer
            fields={fields}
            canAddCustomField={canAddCustomField}
            canDropItem={handleCanDropItem}
            readonlyMode={editDisabled}
          />
          <SortFieldsContainer
            availableFields={availableFields}
            pivotColumns={pivotColumns}
            readonlyMode={editDisabled}
          />
        </ScrollableFlexContainer>
      )}
      {selectedTab === "Settings" && <SettingsContainer readonlyMode={editDisabled} />}
      {selectedTab === "Format" && <FormatContainer readonlyMode={editDisabled} />}
    </Box>
  );
};

export default TableComponent;
